import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Alert,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Paper,
  DialogActions,
  TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const apiUrl = process.env.REACT_APP_API_URL;

const UserList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [deleteUserId, setDeleteUserId] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${apiUrl}/users`);
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setError(t('error_fetching_users'));
      }
    };

    fetchUsers();
  }, [t]);

  const handleViewDetails = (userId) => navigate(`/user-details/${userId}`);
  const handleEditUser = (userId) => navigate(`/useredit/${userId}`);

  const handleOpenChangePassword = (userId) => {
    setSelectedUserId(userId);
    setOpenChangePassword(true);
  };

  const handleCloseChangePassword = () => {
    setOpenChangePassword(false);
    setSelectedUserId(null);
    setNewPassword('');
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleChangePassword = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setError('لم يتم العثور على رمز التحقق. الرجاء تسجيل الدخول مرة أخرى.');
        return;
      }

      if (!newPassword || newPassword.length < 6) {
        setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل.');
        return;
      }

      await axios.put(
        `${apiUrl}/changepasswordbyadmin/${selectedUserId}/password`,
        { password: newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert('✅ تم تغيير كلمة المرور بنجاح');
      handleCloseChangePassword();
    } catch (error) {
      console.error('فشل تغيير كلمة المرور:', error);
      const message =
        error.response?.data?.message ||
        'فشل في تغيير كلمة المرور. حاول مرة أخرى.';
      setError(message);
    }
  };

  const handleOpenDeleteConfirmation = (userId) => {
    setDeleteUserId(userId);
    setDeleteConfirmationOpen(true);
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setConfirmationText('');
  };

  const confirmDelete = async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError(t('please_type_delete_to_confirm'));
      return;
    }

    try {
      await axios.delete(`${apiUrl}/users/${deleteUserId}`);
      setUsers(users.filter((user) => user.user_id !== deleteUserId));
      handleCloseDeleteConfirmation();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError(t('error_deleting_user'));
    }
  };

  const columns = [
    { field: 'id', headerName: '#', width: 50 },
    { field: 'username', headerName: t('username'), width: 150 },
    { field: 'first_name', headerName: t('first_name'), width: 150 },
    { field: 'last_name', headerName: t('last_name'), width: 150 },
    { field: 'email', headerName: t('email'), width: 200 },
    { field: 'phone', headerName: t('phone'), width: 150 },
    {
      field: 'actions',
      headerName: t('actions'),
      width: 200,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleViewDetails(params.row.user_id)}
          >
            <VisibilityIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleEditUser(params.row.user_id)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleOpenDeleteConfirmation(params.row.user_id)}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleOpenChangePassword(params.row.user_id)}
          >
            <LockResetIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const rows = users.map((user, index) => ({
    id: index + 1,
    user_id: user.user_id,
    username: user.username,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    phone: user.phone,
  }));

  return (
    <Container component="main" maxWidth="lg" sx={{ width: '100%' }}>
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t('user_list')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/userregistration')}
          sx={{ alignSelf: 'flex-start', mb: 2 }}
        >
          {t('add_user')}
        </Button>
        <TableContainer component={Paper} sx={{ marginTop: 2.5 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            autoHeight
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                color: '#fff',
                fontWeight: 'bold',
              },
            }}
          />
        </TableContainer>
      </Box>

      {/* تغيير كلمة السر */}
      <Dialog open={openChangePassword} onClose={handleCloseChangePassword}>
        <DialogTitle>{t('change_password')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('new_password')}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleClickShowPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangePassword} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={handleChangePassword} color="primary">
            {t('submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* تأكيد الحذف */}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
      >
        <DialogTitle>{t('confirm_deletion')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('please_type_delete_to_confirm')}
            fullWidth
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirmation} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserList;
